<template lang="html">
  <div v-show="!statistic['_destroy']" class="demographic-segment-row">
    <span v-if="categoryLabel" class="demographic-segment-category">{{ categoryLabel }}</span>
    <combo-box
      v-if="suggestions && suggestions.length > 0"
      v-model="statistic.label"
      class="demographic-segment-label"
      :options="suggestions"
      :placeholder="placeholder"
      @blur="save"
    />
    <input
      v-else
      v-model="statistic.label"
      type="text"
      class="demographic-segment-label"
      :placeholder="placeholder"
      @blur="save"
      @keyup.enter="$event.target.blur()"
    />
    <div class="demographic-segment-value">
      <input
        v-model="statistic.value"
        type="number"
        min="0"
        max="100"
        step="0.1"
        placeholder="0"
        @blur="save"
        @keyup.enter="$event.target.blur()"
      />
      <span class="demographic-segment-percent">%</span>
    </div>
    <span v-if="saving" class="ui active mini inline loader" />
    <a
      v-else
      class="demographic-segment-remove"
      title="Remove"
      @click.stop.prevent="remove"
    >
      <i class="icon times fitted" />
    </a>
  </div>
</template>

<script>
import ComboBox from "components/comboBox.vue";

export default {
  components: {
    ComboBox,
  },
  props: {
    value: {
      type: Object,
      default: function () {
        return {};
      },
    },
    metricType: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: "Segment name",
    },
    categoryLabel: {
      type: String,
      default: "",
    },
    suggestions: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      statistic: this.value,
      saving: false,
    };
  },
  computed: {
    endpoint() {
      return "/statistics";
    },
    url() {
      let url = `${this.endpoint}`;
      if (this.statistic.id) {
        url += `/${this.statistic.id}`;
      }
      return url;
    },
    requestType() {
      return this.statistic.id ? "patch" : "post";
    },
  },
  watch: {
    value(newVal) {
      this.statistic = newVal;
    },
  },
  mounted() {
    this.statistic.metric_type = this.metricType;
    this.statistic.manual_update = true;
  },
  methods: {
    remove() {
      const $this = this;
      // Never saved — nothing to delete server-side, and no data loss to
      // confirm. Just drop the draft row.
      if (!this.statistic.id) {
        $this.$emit("remove", $this.statistic);
        return;
      }
      // eslint-disable-next-line no-alert
      if (window.confirm("Are you sure you want to delete this stat?")) {
        window.$.ajax({
          url: $this.url,
          type: "delete",
          dataType: "json",
          data: {
            id: $this.statistic.id,
          },
          success: function () {
            $this.$emit("remove", $this.statistic);
          },
        });
      }
    },
    save() {
      const $this = this;
      if (!this.statistic.label || !this.statistic.value) return;

      this.statistic.metric_type = this.metricType;
      this.statistic.manual_update = true;

      $this.saving = true;
      window.$.ajax({
        url: $this.url,
        type: $this.requestType,
        dataType: "json",
        data: {
          statistic: $this.statistic,
        },
        success: function (data) {
          $this.$set($this.statistic, "id", data.id);
          $this.$emit("input", $this.statistic);
          $this.$emit("change", $this.statistic);
          $this.saving = false;
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.demographic-segment-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0;
}

.demographic-segment-category {
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--text-muted, #a0aec0);
  width: 4.2rem;
}

// Fomantic applies a bordered/boxed default style to any bare <input>
// (not just ones wrapped in .ui.input) — !important here is a deliberate,
// narrow override for this specific flat inline-editing row, not a general
// pattern.
.demographic-segment-label {
  flex: 1;
  min-width: 0;
  border: none !important;
  border-bottom: 1px solid transparent !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0.25rem 0.1rem !important;
  font-size: 0.92rem;
  color: var(--text-primary, #1a202c);

  &:focus {
    outline: none;
    border-bottom-color: #f4806f !important;
  }

  &::placeholder {
    color: var(--text-muted, #a0aec0);
  }
}

.demographic-segment-value {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  border-bottom: 1px solid var(--border-light, #e2e2e2);

  input {
    width: 2.75rem;
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    text-align: right;
    padding: 0.25rem 0.1rem !important;
    font-size: 0.92rem;
    color: var(--text-primary, #1a202c);

    &:focus {
      outline: none;
    }
  }

  &:focus-within {
    border-bottom-color: #f4806f;
  }
}

.demographic-segment-percent {
  color: var(--text-muted, #a0aec0);
  font-size: 0.85rem;
  margin-left: 0.15rem;
}

.demographic-segment-remove {
  flex: 0 0 auto;
  color: var(--text-muted, #a0aec0);
  cursor: pointer;

  &:hover {
    color: #eb554e;
  }
}
</style>
